import { Easing } from 'chart.js';
import React, { useEffect, useMemo, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';

type Props = {
  value: number
  breaks: readonly [number, number, number]
  reverse?: boolean
  format?: (value: number) => string
}

export default function SpeedoChart({ value, breaks, reverse, format }: Props) {
  const data = useMemo(() => {
    const fn: keyof typeof breaks = reverse ? 'reduceRight' : 'reduce'
    return breaks[fn]<number[]>((a, b, i, arr) => [...a, i ? b - arr[i - 1] : b], [])
  }, [breaks, reverse])

  const max = breaks[breaks.length - 1]
  let v = Math.min(max, Math.max(0, value)) / max
  if (!reverse) {
    v = 1 - v
  }

  const valueRef = useRef(v)
  const textRef = useRef('')

  useEffect(() => {
    valueRef.current = v
    textRef.current = format ? format(value) : value.toFixed(2).toString()
  }, [format, v, value])

  return <Doughnut options={{
    circumference: Math.PI,
    rotation: Math.PI,
    tooltips: {
      callbacks: {
        label(tooltipItem, data) {
          let idx = tooltipItem.index!
          if (reverse) {
            idx = breaks.length - idx - 1
          }

          if (idx === 0) {
            return `<${breaks[0]}`
          } else {
            return `${breaks[idx - 1]} to ${breaks[idx]}`
          }
        },
      },
    },
  }} plugins={[{
    afterDatasetDraw(chart: Chart, easing: Easing) {
      const { ctx, chartArea } = chart;
      if (!ctx) { return }
      const v = valueRef.current

      const w = chartArea.right - chartArea.left
      const h = chart.chartArea.bottom - chartArea.top

      const s = Math.min(w, h * 2)
      const x = (chartArea.left + w / 2) / s
      const y = (chartArea.top + h / 2 + s / 4) / s
      const r = 0.5

      const center = {
        x: x * s,
        y: y * s,
      }

      ctx.beginPath()
      ctx.ellipse(center.x, center.y, 0.025 * s, 0.025 * s, 0, Math.PI, 0)
      ctx.fillStyle = 'black'
      ctx.fill()

      const tip = {
        x: (x + Math.cos(Math.PI * v) * r) * s,
        y: (y - Math.sin(Math.PI * v) * r) * s,
      }

      ctx.beginPath()
      ctx.moveTo(center.x, center.y - 1)
      ctx.lineTo(tip.x, tip.y - 1)
      ctx.lineWidth = 1.5
      ctx.strokeStyle = 'black'
      ctx.stroke()

      const text = textRef.current
      const textWidth = ctx.measureText(text).width
      const textHeight = 10

      const textPos = {
        x: Math.max(chartArea.left, tip.x - textWidth),
        y: Math.max(chartArea.top, tip.y - textHeight),
      }
      ctx.lineWidth = 3
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
      ctx.strokeText(text, textPos.x, textPos.y)
      ctx.fillText(text, textPos.x, textPos.y)
    },
  }]} data={{
    datasets: [{
      data,
      backgroundColor: ['#09994d', '#ffda6e', '#d92d1d'],
    }],
  }} />
}
